module Client.Forms.RimPro

open Client.Components
open Client.Components.Select
open Client.DomainTypes.Msg
open Fable.React
open Fulma
open Shared.Dto.RimPro

open Shared.Infrastructure

type Model = {
    Name: string option
    AirSensor: SelectableSensor option
    RainSensor: SelectableSensor option
    FirstWetnessSensor: SelectableSensor option
    SecondWetnessSensor: SelectableSensor option
    AvailableAirSensors: SelectableSensors
    AvailableRainSensors: SelectableSensors
    AvailableLeafletWetnessSensors: SelectableSensors
    DisclaimerApproved: bool
    RequestRunning: bool
}

let tryCreateDto (model: Model) : NewRimProStationDto option =
    if model.DisclaimerApproved then
        let factory =
            fun
                (name: string)
                (airSensor: SelectableSensor)
                (rainSensor: SelectableSensor)
                (firstWetnessSensor: SelectableSensor) -> {
                NewRimProStationDto.Name = name
                AirSensorId = airSensor.Id
                RainSensorId = rainSensor.Id
                FirstWetnessSensorId = firstWetnessSensor.Id
                SecondWetnessSensorId = model.SecondWetnessSensor |> Option.map (fun x -> x.Id)
            }

        factory
        |> Some
        |> Option.apply model.Name
        |> Option.apply model.AirSensor
        |> Option.apply model.RainSensor
        |> Option.apply model.FirstWetnessSensor
    else
        None

type FormResult =
    | Noop
    | Create of NewRimProStationDto
    | CloseModal

let init airSensors rainSensors (leafletWetnessSensors: SelectableSensors) = {
    Name = None
    AirSensor = SelectableSensors.tryHead airSensors
    RainSensor = SelectableSensors.tryHead rainSensors
    FirstWetnessSensor = SelectableSensors.tryHead leafletWetnessSensors
    SecondWetnessSensor = None
    AvailableAirSensors = airSensors
    AvailableRainSensors = rainSensors
    AvailableLeafletWetnessSensors = leafletWetnessSensors
    DisclaimerApproved = false
    RequestRunning = false
}

let update (msg: RimProFormMsg) (model: Model) =
    match msg with
    | RimProFormMsg.NameUpdated name -> { model with Name = name }, Noop
    | RimProFormMsg.AirSensorUpdated sensor -> { model with AirSensor = sensor }, Noop
    | RimProFormMsg.RainSensorUpdated sensor -> { model with RainSensor = sensor }, Noop
    | RimProFormMsg.FirstWetnessSensorUpdated sensor -> { model with FirstWetnessSensor = sensor }, Noop
    | RimProFormMsg.SecondWetnessSensorUpdated sensor -> { model with SecondWetnessSensor = sensor }, Noop
    | RimProFormMsg.CloseModal -> model, FormResult.CloseModal
    | RimProFormMsg.Create newStation -> { model with RequestRunning = true }, FormResult.Create newStation
    | RimProFormMsg.CreationFinished -> { model with RequestRunning = false }, FormResult.Noop
    | RimProFormMsg.DisclaimerApprovalChanged approved -> { model with DisclaimerApproved = approved }, Noop

let priceInfoField =
    Field.div [] [
        Label.label [] [ str "Preis" ]
        Control.div [] [
            p [] [ str "280 €/Jahr (exklusive Mehrwertsteuer)" ]
        ]
    ]

let private formView dispatch (model: Model) =
    form [] [
        Field.div [] [
            Label.label [] [ str "Name" ]
            Control.div [] [
                Input.text [
                    model.Name |> Option.defaultValue "" |> Input.Value
                    Input.OnChange(fun event -> event.Value |> String.toOption |> RimProFormMsg.NameUpdated |> dispatch)
                ]
            ]
        ]
        Field.div [] [
            Label.label [] [ str "Temperatur Sensor" ]
            Control.div [] [
                SensorSelect.view
                    (RimProFormMsg.AirSensorUpdated >> dispatch)
                    model.AirSensor
                    model.AvailableAirSensors
                    false
            ]
        ]
        Field.div [] [
            Label.label [] [ str "Regen Sensor" ]
            Control.div [] [
                SensorSelect.view
                    (RimProFormMsg.RainSensorUpdated >> dispatch)
                    model.RainSensor
                    model.AvailableRainSensors
                    false
            ]
        ]
        Field.div [] [
            Label.label [] [ str "Blattnässe Sensor 1" ]
            Control.div [] [
                SensorSelect.view
                    (RimProFormMsg.FirstWetnessSensorUpdated >> dispatch)
                    model.FirstWetnessSensor
                    model.AvailableLeafletWetnessSensors
                    false
            ]
        ]

        Field.div [] [
            Label.label [] [ str "Blattnässe Sensor 2 (optional)" ]
            Control.div [] [
                SensorSelect.view
                    (RimProFormMsg.SecondWetnessSensorUpdated >> dispatch)
                    model.SecondWetnessSensor
                    model.AvailableLeafletWetnessSensors
                    true
            ]
        ]

        priceInfoField

        Disclaimer.disclaimer model.DisclaimerApproved (RimProFormMsg.DisclaimerApprovalChanged >> dispatch)
    ]

let private saveButton dispatch (model: Model) =
    let maybeNewStation = tryCreateDto model

    let buttonOptions = [
        Button.IsLoading model.RequestRunning
        Button.Color IsSuccess

        match maybeNewStation with
        | Some newStation -> Button.OnClick(fun _ -> dispatch (RimProFormMsg.Create newStation))
        | None -> Button.Disabled true
    ]

    Button.button buttonOptions [ str "Erstellen" ]

let view (dispatch: RimProFormMsg -> unit) (model: Model) =
    let closeModal = (fun _ -> dispatch RimProFormMsg.CloseModal)

    Modal.modal [ Modal.IsActive true ] [
        Modal.background [] []
        Modal.Card.card [] [
            Modal.Card.head [] [
                Modal.Card.title [] [ str "Neue RimPro Station erstellen" ]
                Delete.delete [ Delete.OnClick closeModal ] []
            ]
            Modal.Card.body [] [ Content.content [] [ formView dispatch model ] ]
            Modal.Card.foot [] [ saveButton dispatch model ]
        ]
    ]